import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import type { Task, Client, Project } from '@/types';
import { isTaskOverdue } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/ui/data-table';
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Calendar,
  AlertCircle,
  CheckCircle2,
  Circle,
  Clock,
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface TasksTableProps {
  tasks: Task[];
  clients: Client[];
  projects: Project[];
  isLoading?: boolean;
  onDelete?: (task: Task) => void;
  emptyState?: React.ReactNode;
}

const priorityConfig = {
  high: { label: 'High', className: 'border-red-500 text-red-500' },
  medium: { label: 'Medium', className: 'border-muted-foreground text-muted-foreground' },
  low: { label: 'Low', className: 'border-green-600 text-green-600' },
};

const statusConfig = {
  'not-started': { label: 'Not Started', icon: Circle, className: 'text-muted-foreground' },
  'in-progress': { label: 'In Progress', icon: Clock, className: 'text-blue-600 dark:text-blue-400' },
  'completed': { label: 'Completed', icon: CheckCircle2, className: 'text-green-600 dark:text-green-400' },
};

export function TasksTable({
  tasks,
  clients,
  projects,
  isLoading,
  onDelete,
  emptyState,
}: TasksTableProps) {
  const navigate = useNavigate();

  const getClientName = (clientId?: string) => {
    if (!clientId) return null;
    return clients.find((c) => c.id === clientId)?.name || null;
  };

  const getProjectName = (projectId?: string) => {
    if (!projectId) return null;
    return projects.find((p) => p.id === projectId)?.name || null;
  };

  const columns: ColumnDef<Task>[] = [
    {
      accessorKey: 'title',
      header: 'Task',
      cell: ({ row }) => {
        const task = row.original;
        const clientName = getClientName(task.clientId);
        const projectName = getProjectName(task.projectId);

        return (
          <div className="min-w-[200px]">
            <Link
              to={`/tasks/${task.id}`}
              className="font-medium text-foreground hover:text-primary transition-colors"
            >
              {task.title}
            </Link>
            {(clientName || projectName) && (
              <div className="flex items-center gap-2 mt-1 text-sm text-muted-foreground">
                {clientName && <span>{clientName}</span>}
                {clientName && projectName && <span>·</span>}
                {projectName && <span>{projectName}</span>}
              </div>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => {
        const status = row.getValue('status') as keyof typeof statusConfig;
        const config = statusConfig[status];
        const Icon = config.icon;

        return (
          <div className="flex items-center gap-2">
            <Icon className={cn('h-4 w-4', config.className)} />
            <span className={cn('text-sm', config.className)}>{config.label}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'priority',
      header: 'Priority',
      cell: ({ row }) => {
        const priority = row.getValue('priority') as keyof typeof priorityConfig;
        const config = priorityConfig[priority];

        return (
          <Badge className={config.className}>
            {config.label}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'deadline',
      header: 'Deadline',
      cell: ({ row }) => {
        const task = row.original;
        const deadline = task.deadline;
        const overdue = isTaskOverdue(task);

        if (!deadline) {
          return <span className="text-muted-foreground">—</span>;
        }

        const date = new Date(deadline);
        const formatted = date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        });

        return (
          <div className={cn('flex items-center gap-2', overdue && 'text-destructive')}>
            {overdue && <AlertCircle className="h-4 w-4" />}
            <Calendar className={cn('h-4 w-4', !overdue && 'text-muted-foreground')} />
            <span className={overdue ? 'font-medium' : ''}>{formatted}</span>
          </div>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const task = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/tasks/${task.id}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete?.(task)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={tasks}
      isLoading={isLoading}
      emptyState={emptyState}
      pageSize={10}
    />
  );
}
